// The depth look: the two pictures of the depth bands (R2713, R2726,
// mockup round 116, ui-map-interface.md §17.3.09).
//
// The FEATHERED look (card 02) blends the band alphas over a cell and its
// six neighbours, so each band edge is a soft slope; the shoal stipple
// (card 71) and the edge seam (card 72) are chart pieces that draw on it
// while their gates hold. The STEPPED look (card 01) gives each cell the
// alpha of its band, a hard hex step, and nothing more (the lead's ruling,
// R2726). The DEPTH heat map (R2870) draws a third picture, the depth heat
// look: one ink and one alpha for each band, a hard step and a thin
// contour on the shallower side of each band edge.
// `[chart.piece.depth_tints]`, `[chart.piece.shoal_stipple]` and
// `[chart.piece.edge_seam]` of `data/presentation/chart.toml` hold every
// figure.
//
// The World field `depth` of the theme picks the look (themes.md §3.4).
// The key `\` flips it for the session alone: the browser keeps nothing,
// so every load draws the pick of the theme (R2726).
//
// This module opens no GL and no DOM, so the legend, the route profile,
// the depth pass and the tests read one answer.

import { CHART } from './gen/presentation';
import { DEPTH_STYLES } from './gen/balance';
import type { ChartReading } from './fast/chart/context';
import { channels } from './render/color';
import { rgbToOklab, type Rgb } from './ui/theme';

/** The two looks: card 02 and card 01 of round 116. */
export type DepthLook = (typeof DEPTH_STYLES)[number];

/** The figures of the depth tints, the stipple and the seam. */
const T = CHART.pieces.depth_tints;
const STIPPLE = CHART.pieces.shoal_stipple;
const SEAM = CHART.pieces.edge_seam;

/** The AUTO look, position 0 of the list: card 02, feathered. */
export const DEPTH_LOOK_DEFAULT: DepthLook = DEPTH_STYLES[0];

/** The most own hulls that light a seam in one frame (even). */
export const SEAM_HULL_SLOTS = 2 * Math.ceil(SEAM.fig.hulls / 2);

/** The most dots one cell carries; the pass loops to it. */
export const STIPPLE_MAX = Math.max(1, Math.floor(STIPPLE.fig.max));

/** The other look. */
function other(l: DepthLook): DepthLook {
  return l === 'feathered' ? 'stepped' : 'feathered';
}

/** The key `\` flipped the look an odd number of times this session. */
let flipped = false;
/** The pick of the game page's theme (`fast/main.ts`). */
let pagePick: DepthLook = DEPTH_LOOK_DEFAULT;

/** The look a scene draws for a theme pick: the pick, or the other look
 *  while the key flipped it. */
export function depthLookOf(pick: DepthLook): DepthLook {
  return flipped ? other(pick) : pick;
}

/** The look the game map draws now: the page's pick, flipped by the key. */
export function depthLook(): DepthLook { return depthLookOf(pagePick); }

/** The game page states the pick of its theme. */
export function setDepthPick(pick: DepthLook): void { pagePick = pick; }

/** The press of `\`: the other look for this session. Answers the look the
 *  game map draws now. Each scene reads the flip on its next frame
 *  (`MapScene.setVisibility`). */
export function toggleDepthLook(): DepthLook {
  flipped = !flipped;
  return depthLook();
}

/** Takes the flip and the page pick back to the start. For the tests. */
export function resetDepthLook(): void {
  flipped = false;
  pagePick = DEPTH_LOOK_DEFAULT;
}

/** What one look paints in one reading. */
export interface DepthLookCfg {
  /** Blend the alphas over the ring (card 02), or step them (card 01). */
  feathered: boolean;
  /** The tint alpha of each band, shelf to abyss, the reading's scale in. */
  alphas: readonly [number, number, number, number];
}

/** The four band figures of an array of the pack, padded with 0. */
function four(a: readonly number[], k: number): [number, number, number, number] {
  return [(a[0] ?? 0) * k, (a[1] ?? 0) * k, (a[2] ?? 0) * k, (a[3] ?? 0) * k];
}

/** What a look paints in a reading, out of the pack. */
export function depthLookCfg(l: DepthLook, reading: ChartReading): DepthLookCfg {
  const r = T[reading];
  if (l === 'stepped') return { feathered: false, alphas: four(r.arr.step_alpha, r.alpha) };
  return { feathered: true, alphas: four(r.arr.band_alpha, r.alpha * T.fig.feather_gain) };
}

/** The tint alpha of one band in a look. The legend swatches and the
 *  route profile read it, so they show the alphas the map paints. */
export function depthAlpha(band: number, reading: ChartReading, l: DepthLook = depthLook()): number {
  return depthLookCfg(l, reading).alphas[band - 1] ?? 0;
}

/** What the DEPTH heat look paints in one reading (R2870). */
export interface DepthHeatCfg {
  /** The ink of each band, shelf to abyss, as packed 0xRRGGBB. */
  inks: readonly [number, number, number, number];
  /** The alpha of each band, the reading's scale in. */
  alphas: readonly [number, number, number, number];
  /** The contour on the shallower side of a band edge: its alpha, and its
   *  width in screen px. */
  edgeAlpha: number;
  edgePx: number;
}

/** The DEPTH heat look in a reading, out of the pack. The depth pass, the
 *  legend swatches and the ramp of the DEPTH heat map read it. */
export function depthHeatCfg(reading: ChartReading): DepthHeatCfg {
  const r = T[reading];
  const ink = (i: number): number => Math.round(r.arr.heat_ink[i] ?? 0) & 0xffffff;
  return {
    inks: [ink(0), ink(1), ink(2), ink(3)],
    alphas: four(r.arr.heat_alpha, r.alpha),
    edgeAlpha: T.fig.heat_edge_alpha,
    edgePx: T.fig.heat_edge_px,
  };
}

/** The water the depth heat look is measured over: the deep foot of the
 *  water gradient, the ground of the heat hues too (R2868). */
export const HEAT_WATER = 0x020b12;

/**
 * The OKLab lightness of each band of the depth heat look over `water`,
 * shelf to abyss: the band ink at its alpha, blended on the sRGB values as
 * the premultiplied pass blends them (R2870). The palette must step each
 * neighbour pair by at least 0.08, so the four bands read apart.
 */
export function depthHeatLightness(reading: ChartReading, water = HEAT_WATER): number[] {
  const cfg = depthHeatCfg(reading);
  const w = channels(water);
  return cfg.inks.map((ink, i) => {
    const a = cfg.alphas[i], c = channels(ink);
    return rgbToOklab([0, 1, 2].map((k) => c[k] * a + w[k] * (1 - a)) as Rgb)[0];
  });
}
